import React, { FunctionComponent, useCallback, useState } from 'react';
import classNames from 'classnames';
import type { Recruitment } from '../../../types/personShow';
import { Button } from '../../atoms/button';
import { SendTemplate } from '../../organisms/sendTemplate';
import { ScoutNgWordsModal } from '../scoutNgWordsModal';

type Props = {
  isAllowedToScout: boolean;
  hasRecruitments: boolean;
  selectedRecruitment?: Recruitment;
  visibleRecruitments: Recruitment[];
  targetRecruitment?: number | null;
  isRecruitmentSelectOpen: boolean;
  recruitmentKeyword: string;
  scoutButtonDisabled: boolean;
  recruitmentSelectRef: React.RefObject<HTMLDivElement>;
  messageBody: string;
  toggleRecruitmentSelect: () => void;
  changeRecruitmentKeyword: (keyword: string) => void;
  selectRecruitment: (recruitmentId: number) => void;
  changeMessageBody: (message: string) => void;
  handleSubmit: () => void;
};

export const Presentation: FunctionComponent<Props> = ({
  isAllowedToScout,
  hasRecruitments,
  selectedRecruitment,
  visibleRecruitments,
  targetRecruitment,
  isRecruitmentSelectOpen,
  recruitmentKeyword,
  scoutButtonDisabled,
  recruitmentSelectRef,
  messageBody,
  toggleRecruitmentSelect,
  changeRecruitmentKeyword,
  selectRecruitment,
  changeMessageBody,
  handleSubmit,
}) => {
  const [isNgWordsModalOpen, setIsNgWordsModalOpen] = useState(false);
  const closeNgWordsModal = useCallback(() => setIsNgWordsModalOpen(false), []);

  const renderContent = () => {
    if (!isAllowedToScout) {
      return (
        <p className="scout-send-form__notice scout-send-form__notice--plan">
          現在のプランではスカウトできません。もしスカウトをご希望の場合は
          <a href="/accounts/plan_change">プランをアップグレード</a>
          してご利用ください。
        </p>
      );
    }

    if (!hasRecruitments) {
      return <p className="scout-send-form__notice">スカウトに紐づられる求人がありません</p>;
    }

    return (
      <>
        <p className="scout-send-form__description">
          ※ガイドラインや利用規約に違反する行為があった場合は、サービスの利用を停止させていただく場合がございます<br />
          ※メッセージ内で外部サイトに誘導することはご遠慮ください
        </p>
        <p className="scout-send-form__label" id="scout-recruitment-label">紐づける求人を選択</p>
        <div className="scout-send-form__select" ref={recruitmentSelectRef}>
          <button
            type="button"
            className="scout-send-form__select-trigger"
            aria-haspopup="listbox"
            aria-expanded={isRecruitmentSelectOpen}
            onClick={toggleRecruitmentSelect}
          >
            <span>
              {selectedRecruitment?.[1] || 'スカウトに紐づける求人を選択してください'}
            </span>
            <svg className={classNames('scout-send-form__select-arrow', { 'is-open': isRecruitmentSelectOpen })} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="267.69 -596.31 424.62 235.62" width="10px" fill="#19190F"><path d="M480-360.69 267.69-573 291-596.31l189 189 189-189L692.31-573 480-360.69Z"/></svg>
          </button>
          {isRecruitmentSelectOpen && (
            <div className="scout-send-form__select-dropdown">
              <input
                type="search"
                value={recruitmentKeyword}
                placeholder="絞り込む"
                aria-label="求人を絞り込む"
                onChange={(event) => changeRecruitmentKeyword(event.currentTarget.value)}
                autoFocus
              />
              <ul role="listbox" aria-labelledby="scout-recruitment-label">
                {visibleRecruitments.length > 0 ? (
                  visibleRecruitments.map(([id, title]) => (
                    <li key={id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={id === targetRecruitment}
                        title={title}
                        onClick={() => selectRecruitment(id)}
                      >
                        {title}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="scout-send-form__select-empty">
                    該当する求人はありません
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
        <div className="scout-send-form__label-row">
          <label className="scout-send-form__label" htmlFor="scout-message">メッセージを入力してください</label>
          <SendTemplate
            targetArea=""
            className="scout-send-form__template-link"
            onApplyTemplate={changeMessageBody}
          />
        </div>
        <textarea
          id="scout-message"
          className="scout-send-form__message"
          value={messageBody}
          placeholder="スカウトメッセージを入力してください。"
          onChange={(event) => changeMessageBody(event.currentTarget.value)}
        ></textarea>
        <button
          type="button"
          className="scout-send-form__ng-words-link"
          onClick={() => setIsNgWordsModalOpen(true)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">help</span>
          <span>メッセージで送信できないワード</span>
        </button>
        <div className="scout-send-form__actions">
          <Button
            className={classNames('scout-send-form__submit', { disabled: scoutButtonDisabled })}
            onClicked={handleSubmit}
            defaultValue="スカウトを送る"
            disabled={scoutButtonDisabled}
          />
        </div>
      </>
    );
  };

  return (
    <section className="scout-send-form">
      <h2 className="scout-send-form__title">スカウト</h2>
      {renderContent()}
      <ScoutNgWordsModal isOpen={isNgWordsModalOpen} onClose={closeNgWordsModal} />
    </section>
  );
};
